import { createContext, useContext, useEffect, useState } from 'react'
import { RequestService } from '../services/RequestService'
import { useSnackbar } from 'notistack'
import { normalizeString } from '../utils/normalizeString'

export const TYPE = {
  STATUS: 'status',
  USER: 'user'
}

const UserListActionsModalContext = createContext()

export const UserListActionsModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState(TYPE.STATUS)
  const [users, setUsers] = useState([])
  const [status, setStatus] = useState(null)
  const [shift, setShift] = useState(null)
  const [supervisionMode, setSupervisionMode] = useState(null)
  const [userList, setUserList] = useState([])
  const [shiftOptions, setShiftOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [timezoneOptions, setTimezoneOptions] = useState([])
  const [caffeineOptions, setCaffeineOptions] = useState([])
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedUser, setSelectedUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const { enqueueSnackbar } = useSnackbar()
  const [supervisor, setSupervisor] = useState([])

  const resetModalState = () => {
    setSelectedTab(0)
    setStatus('')
    setShift('')
    setSupervisor([])
    setSupervisionMode('')
    setSelectedUser('')
    setSelectedUser([])
    setUserList([])
  }

  const openActionsModal = ({ type }) => {
    resetModalState()
    setOpen(true)
    setType(type)
  }

  const closeActionsModal = () => {
    setSelectedTab(0)
    resetModalState()
    setOpen(false)
  }

  const removeUserFromList = ({ id }) => {
    const newList = users.filter(user => user.id !== id)
    setUsers(newList)
  }

  const validateDisabledButton = () => {
    const sameAttributesCheck = users?.length > 0

    if ((
      (selectedTab === 0 && sameAttributesCheck) ||
      (selectedTab === 1 && sameAttributesCheck) ||
      (selectedTab === 2 && sameAttributesCheck)
    )) {
      return false
    }

    return true
  }

  const handleSendClick = async () => {
    const usersId = users.map(user => user.id)
    const snackbar = () =>
      enqueueSnackbar('Sucesso!', {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      })

    await RequestService.updateUserList({
      users: usersId,
      status: status,
      shift: shift,
      supervisors: supervisor,
      supervisorAction: supervisionMode,
    })

    snackbar()
    closeActionsModal()
  }

  const handleUserSendClick = async ({ userData }) => {
    const snackbar = () =>
      enqueueSnackbar('Sucesso!', {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      })

    if (selectedTab === 0) {
      await RequestService.createUser({
        userData: userData,
      })
    } else if (selectedTab === 1) {
      await RequestService.updateUser({
        userData: userData,
      })
    }

    snackbar()
    closeActionsModal()
  }

  const selectUser = ({ id, name }) => {
    const user = users.find(user => user.id === id)

    if (!user) {
      const withNewUser = [...users, { id, name }]
      const sortedList = sortUserList({ list: withNewUser })

      setUsers(sortedList)
      return
    }
  }

  const sortUserList = ({ list }) => {
    return list.sort((a, b) => {
      const nameA = normalizeString({ term: a.name })
      const nameB = normalizeString({ term: b.name })

      if (nameA < nameB) {
        return -1
      }

      if (nameA > nameB) {
        return 1
      }
    })
  }

  useEffect(() => {
    if (open) {
      setLoading(true)

      const fetchUsers = async () => {
        const { data } = await RequestService.getPreSelectedUsers({
          url: link,
        })

        if (data.length > 0) {
          const sortedList = sortUserList({ list: data })
          setUsers(sortedList)
        } else {
          setUsers(data)
        }

        setLoading(false)
      }

      const fetchAllUsers = async () => {
        const { data } = await RequestService.getUsers({ visibility: 'management' })

        if (data.length > 0) {
          const sortedList = sortUserList({ list: data })
          setUserList(sortedList)
        } else {
          setUserList(data)
        }

        fetchUserListData()
        setLoading(false)
      }

      const fetchUserListData = async () => {
        const { data } = await RequestService.getUserListActionsData()
        setShiftOptions(data?.data?.shifts || [])
        setUserOptions(data?.data?.supervisors || [])
        setTimezoneOptions(data?.data?.timezones || [])
        setCaffeineOptions(data?.data?.caffeine_products || [])
      }

      fetchAllUsers()

    }
  }, [open])

  return (
    <UserListActionsModalContext.Provider
      value={{
        open,
        openActionsModal,
        closeActionsModal,
        selectedTab,
        setSelectedTab,
        status,
        setStatus,
        shift,
        setShift,
        supervisionMode,
        setSupervisionMode,
        users,
        userList,
        loading,
        removeUserFromList,
        validateDisabledButton,
        handleSendClick,
        selectUser,
        selectedUser,
        setSelectedUser,
        shiftOptions,
        userOptions,
        supervisor,
        setSupervisor,
        type,
        handleUserSendClick,
        timezoneOptions,
        caffeineOptions,
      }}
    >
      {children}
    </UserListActionsModalContext.Provider>
  )
}

export const useUserListActionsModal = () => {
  return useContext(UserListActionsModalContext)
}
